import { useCallback, useEffect, useState } from 'react';

const DEFAULT_LANGUAGE_CODE = 'EN';
const DEFAULT_CURRENCY_CODE = 'INR';

const normalizeKey = (value: string) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

const LANGUAGE_ALIASES: Record<string, string> = {
  EN: 'en',
  ENGLISH: 'en',
  HI: 'hi',
  HINDI: 'hi',
  TA: 'ta',
  TAMIL: 'ta',
  TE: 'te',
  TELUGU: 'te',
  KN: 'kn',
  KANNADA: 'kn',
  ML: 'ml',
  MALAYALAM: 'ml',
  BN: 'bn',
  BENGALI: 'bn',
  MR: 'mr',
  MARATHI: 'mr',
};

const LOCALE_MAP: Record<string, string> = {
  en: 'en-IN',
  hi: 'hi-IN',
  ta: 'ta-IN',
  te: 'te-IN',
  kn: 'kn-IN',
  ml: 'ml-IN',
  bn: 'bn-IN',
  mr: 'mr-IN',
};

const DEFAULT_CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

const TRANSLATIONS: Record<string, Record<string, string>> = {
  en: {
    'cart.title': 'Your Cart',
    'cart.empty': 'Your cart is empty',
    'cart.coupon.placeholder': 'Enter coupon code',
    'cart.coupon.apply': 'Apply',
    'cart.coupon.applied': 'applied',
    'cart.delivery.restaurant': 'Delivery by Restaurant',
    'cart.delivery.partner': 'Delivery by Delivery Partner',
    'cart.summary.itemTotal': 'Item Total',
    'cart.summary.deliveryFee': 'Delivery Fee',
    'cart.summary.addMore': 'Add {amount} more for free delivery',
    'cart.summary.packaging': 'Packaging Charges',
    'cart.summary.discount': 'Discount',
    'cart.summary.taxes': 'Taxes & Charges',
    'cart.summary.toPay': 'To Pay',
    'cart.checkout.processing': 'Processing Order...',
    'cart.checkout.cta': 'Proceed to Checkout • {amount}',
    'common.free': 'FREE',
    'common.add': 'ADD',
    'menu.customisable': 'customisable',
    'menu.offer.flat': 'FLAT 50% OFF',
    'menu.offer.getFor': 'Get for {amount}',
    'menu.badge.reordered': 'Highly reordered',
    'menu.badge.chef': 'Chef Special',
    'menu.badge.recommended': 'Recommended',
    'menu.tag.seasonal': 'Seasonal',
    'profile.quick.orders': 'Orders',
    'profile.quick.favorites': 'Favorites',
    'profile.quick.saved': 'Saved',
  },
  hi: {
    'cart.title': 'आपकी कार्ट',
    'cart.empty': 'आपकी कार्ट खाली है',
    'cart.coupon.placeholder': 'कूपन कोड दर्ज करें',
    'cart.coupon.apply': 'लागू करें',
    'cart.coupon.applied': 'लागू हुआ',
    'cart.delivery.restaurant': 'रेस्टोरेंट द्वारा डिलीवरी',
    'cart.delivery.partner': 'डिलीवरी पार्टनर द्वारा डिलीवरी',
    'cart.summary.itemTotal': 'आइटम कुल',
    'cart.summary.deliveryFee': 'डिलीवरी शुल्क',
    'cart.summary.addMore': 'फ्री डिलीवरी के लिए {amount} और जोड़ें',
    'cart.summary.packaging': 'पैकेजिंग शुल्क',
    'cart.summary.discount': 'छूट',
    'cart.summary.taxes': 'कर और शुल्क',
    'cart.summary.toPay': 'देय राशि',
    'cart.checkout.processing': 'ऑर्डर प्रोसेस हो रहा है...',
    'cart.checkout.cta': 'चेकआउट करें • {amount}',
    'common.free': 'फ्री',
    'common.add': 'जोड़ें',
    'menu.customisable': 'कस्टमाइज़ करें',
    'menu.offer.flat': 'फ्लैट 50% छूट',
    'menu.offer.getFor': '{amount} में पाएं',
    'menu.badge.reordered': 'अधिक री-ऑर्डर',
    'menu.badge.chef': 'शेफ स्पेशल',
    'menu.badge.recommended': 'सिफ़ारिश',
    'menu.tag.seasonal': 'मौसमी',
    'profile.quick.orders': 'ऑर्डर्स',
    'profile.quick.favorites': 'पसंदीदा',
    'profile.quick.saved': 'बचत',
  },
};

export interface LanguagePreference {
  code: string;
  key: string;
  name: string;
  locale: string;
}

export interface CurrencyPreference {
  code: string;
  name: string;
  symbol: string;
}

const resolveLanguageKey = (code: string) => {
  const normalized = normalizeKey(code || DEFAULT_LANGUAGE_CODE);
  return LANGUAGE_ALIASES[normalized] || normalized.slice(0, 2).toLowerCase() || 'en';
};

export const readLanguagePreference = (): LanguagePreference => {
  const stored = localStorage.getItem('language') || DEFAULT_LANGUAGE_CODE;
  const name = localStorage.getItem('language_name') || stored;
  const key = resolveLanguageKey(stored);
  const locale = LOCALE_MAP[key] || LOCALE_MAP.en;
  return { code: normalizeKey(stored) || DEFAULT_LANGUAGE_CODE, key, name, locale };
};

export const readCurrencyPreference = (): CurrencyPreference => {
  const stored = localStorage.getItem('currency') || DEFAULT_CURRENCY_CODE;
  const normalized = normalizeKey(stored) || DEFAULT_CURRENCY_CODE;
  const name = localStorage.getItem('currency_name') || stored;
  const symbol =
    localStorage.getItem('currency_symbol') ||
    DEFAULT_CURRENCY_SYMBOLS[normalized] ||
    '';
  return { code: normalized, name, symbol };
};

export const translate = (key: string, languageCode?: string, fallback?: string) => {
  const langKey = resolveLanguageKey(languageCode || DEFAULT_LANGUAGE_CODE);
  const dictionary = TRANSLATIONS[langKey] || TRANSLATIONS.en;
  return dictionary[key] || TRANSLATIONS.en[key] || fallback || key;
};

export const formatCurrency = (
  amount: number | null | undefined,
  prefs: { code: string; symbol?: string; locale?: string },
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number; withSymbol?: boolean }
) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';

  const locale = prefs.locale || LOCALE_MAP.en;
  const symbol = prefs.symbol || DEFAULT_CURRENCY_SYMBOLS[prefs.code] || '';
  const maximumFractionDigits =
    typeof options?.maximumFractionDigits === 'number'
      ? options.maximumFractionDigits
      : Number.isInteger(value)
        ? 0
        : 2;
  const minimumFractionDigits = options?.minimumFractionDigits ?? 0;

  const numberFormatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
  const formatted = numberFormatter.format(Math.abs(value));
  const sign = value < 0 ? '-' : '';

  if (options?.withSymbol === false) {
    return `${sign}${formatted}`;
  }

  if (symbol) {
    return `${sign}${symbol}${formatted}`;
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: prefs.code,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value);
  } catch {
    return `${sign}${formatted}`;
  }
};

export const formatCompactCurrency = (
  amount: number | null | undefined,
  prefs: { code: string; symbol?: string; locale?: string }
) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  const locale = prefs.locale || LOCALE_MAP.en;
  const symbol = prefs.symbol || DEFAULT_CURRENCY_SYMBOLS[prefs.code] || '';
  const compact = new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Math.abs(value));
  const sign = value < 0 ? '-' : '';
  return symbol ? `${sign}${symbol}${compact}` : `${sign}${compact}`;
};

export const useLocale = () => {
  const [language, setLanguage] = useState<LanguagePreference>(() => readLanguagePreference());
  const [currency, setCurrency] = useState<CurrencyPreference>(() => readCurrencyPreference());

  useEffect(() => {
    const handleLanguageChange = () => setLanguage(readLanguagePreference());
    const handleCurrencyChange = () => setCurrency(readCurrencyPreference());
    const handleStorage = (event: StorageEvent) => {
      if (!event.key) return;
      if (event.key.startsWith('language')) handleLanguageChange();
      if (event.key.startsWith('currency')) handleCurrencyChange();
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    window.addEventListener('currencyChange', handleCurrencyChange as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
      window.removeEventListener('currencyChange', handleCurrencyChange as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const t = useCallback(
    (key: string, fallback?: string, replacements?: Record<string, string>) => {
      let value = translate(key, language.code, fallback);
      if (replacements) {
        Object.entries(replacements).forEach(([token, replacement]) => {
          value = value.replace(new RegExp(`{${token}}`, 'g'), replacement);
        });
      }
      return value;
    },
    [language.code]
  );

  const formatMoney = useCallback(
    (amount: number | null | undefined, options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }) =>
      formatCurrency(
        amount,
        { code: currency.code, symbol: currency.symbol, locale: language.locale },
        options
      ),
    [currency.code, currency.symbol, language.locale]
  );

  const formatCompactMoney = useCallback(
    (amount: number | null | undefined) =>
      formatCompactCurrency(amount, { code: currency.code, symbol: currency.symbol, locale: language.locale }),
    [currency.code, currency.symbol, language.locale]
  );

  return {
    language,
    currency,
    t,
    formatMoney,
    formatCompactMoney,
  };
};
